<template>
  <div v-if="localGameState || (gameState && game && duel)">
    <q-dialog
      v-if="gameState && game && duel"
      v-model="predictionModalOpened"
      transition-show="scale"
      transition-hide="scale"
      persistent
      maximized
    >
      <div
        class="modal-prediction bg-white"
        style="width: 480px;"
      >
        <header class="modal-prediction__header q-pa-sm">
          <h2 class="predictions__title text-h6 text-center">
            {{ $t('titles.modal_prediction_title') }}:
          </h2>
          <div
            v-if="nextAtBat"
            class="predictions__pair row justify-between items-center text-center"
          >
            <div class="col-5 text-right">
              <p>{{ nextAtBat.awayPlayer.name }}</p>
              <p>
                {{ game.awayTeam.abbreviation }}
                -
                <template v-if="nextAtBat.awayPlayer.jerseyNumber >= 0">
                  #{{ nextAtBat.awayPlayer.jerseyNumber }}
                </template>
                <template v-else>
                  {{ $t('NA') }}
                </template>
              </p>
            </div>
            <span class="col-2 text-secondary">{{ $t('vs') }}</span>
            <div class="col-5 text-left">
              <p>{{ nextAtBat.homePlayer.name }}</p>
              <p>
                {{ game.homeTeam.abbreviation }}
                -
                <template v-if="nextAtBat.homePlayer.jerseyNumber >= 0">
                  #{{ nextAtBat.homePlayer.jerseyNumber }}
                </template>
                <template v-else>
                  {{ $t('NA') }}
                </template>
              </p>
            </div>
          </div>
        </header>
        <q-tabs
          v-model="predictionTab"
          inline-label
          dense
          stretch
          align="justify"
          class="modal-prediction__tabs bg-grey-4 text-black tabs-fixed"
        >
          <q-tab
            :label="$t('HIT')"
            name="HIT"
            alert="primary"
          />
          <q-tab
            :label="$t('OUT')"
            name="OUT"
            alert="red-8"
          />
          <q-tab
            :label="$t('WALK')"
            name="WALK"
            alert="blue-8"
          />
        </q-tabs>
        <q-tab-panels
          v-model="predictionTab"
          stretch
          style="flex-basis: 100%;"
          class="modal-prediction__panels"
        >
          <q-tab-panel
            :key="tab"
            v-for="tab in Object.keys(predictionsTypesParsed)"
            :name="tab"
          >
            <ul
              v-if="predictionsTypesParsed[tab].items"
              :class="{
                'solo-first-item': tab === 'HIT',
              }"
              class="grid-predictions columns-2 q-pa-sm"
            >
              <li
                :key="item.code"
                v-for="item in predictionsTypesParsed[tab].items"
              >
                <selectable-prediction
                  :code="item.code"
                  :name="getTabLabel(item.name)"
                  :ratio="item.rate"
                  v-model="selectedPrediction"
                  :activeColor="activeColors[tab]"
                />
              </li>
            </ul>
            <template v-else>
              <q-tabs
                v-model="predictionOutTab"
                inline-label
                dense
                no-caps
                align="justify"
                class="bg-grey-5 text-negative tabs-fixed"
              >
                <q-tab
                  :key="subTab"
                  v-for="subTab in Object.keys(predictionsTypesParsed[tab])"
                  :label="getTabLabel(subTab)"
                  :name="subTab"
                />
              </q-tabs>
              <q-tab-panels v-model="predictionOutTab">
                <q-tab-panel
                  :key="subTab"
                  v-for="subTab in Object.keys(predictionsTypesParsed[tab])"
                  :name="subTab"
                >
                  <ul
                    :class="{
                      'solo-first-item': ['GROUND_OUT', 'FLY_OUT'].includes(
                        subTab
                      ),
                      'solo-last-item': subTab === 'GROUND_OUT',
                      'columns-2': subTab === 'STRIKE_OUT',
                    }"
                    class="grid-predictions q-pa-sm"
                  >
                    <li
                      :key="item.code"
                      v-for="item in predictionsTypesParsed[tab][subTab].items"
                    >
                      <selectable-prediction
                        :code="item.code"
                        :name="getTabLabel(item.name)"
                        :ratio="item.rate"
                        v-model="selectedPrediction"
                        :activeColor="activeColors[tab]"
                      />
                    </li>
                  </ul>
                </q-tab-panel>
              </q-tab-panels>
            </template>
          </q-tab-panel>
        </q-tab-panels>

        <div class="modal-prediction__points q-py-sm q-px-md">
          <div class="text-body2 text-uppercase">
            <p>
              <i18n
                path="statuses.points"
                tag="span"
              >
                <span
                  place="points"
                  class="text-primary text-bold"
                >{{
                  userCurrentPoints
                }}</span>
              </i18n>
              -
              <i18n
                path="statuses.rank"
                tag="span"
              >
                <span
                  place="rank"
                  class="text-primary text-bold"
                >#{{ userRank }}</span>
              </i18n>
            </p>
          </div>
          <h3 class="text-h6">
            {{ $t('titles.points') }}: {{ selectedPoints }}
          </h3>
          <ul class="list-points">
            <li v-for="points in POINTS_FIXED_LIST">
              <selectable-points
                :points="points"
                v-model="selectedPoints"
                :activeColor="'red-6'"
              />
            </li>
            <li>
              <q-btn
                @click="openCustomPointsModal"
                @keydown.space="openCustomPointsModal"
                :color="isSelectedCustomPoints ? 'orange-5' : 'grey-3'"
                :text-color="isSelectedCustomPoints ? 'black' : 'grey-9'"
                full-width
                round
                icon="build"
                size="md"
              />
            </li>
          </ul>
        </div>

        <div class="modal-prediction__actions form-actions q-py-sm q-px-md">
          <q-btn
            @click="makePrediction"
            :label="$t('buttons.make_prediction')"
            :disabled="makePredictionDisabled"
            class="full-width"
            color="teal-7"
          />
          <q-btn
            :label="$t('buttons.cancel')"
            v-close-popup
            flat
            class="full-width"
            text-color="teal-7"
          />
        </div>
      </div>
    </q-dialog>

    <header class="page-duel__header q-py-sm text-center">
      <q-btn
        v-if="
          gameState &&
            game &&
            duel &&
            game.status !== GAME_STATUSES.COMPLETED &&
            duel.status !== DUEL_STATUSES.CLOSED
        "
        @click="leaveDuel"
        :label="$t('buttons.leave_the_duel')"
        color="negative"
        class="btn-leave-duel"
        dense
        flat
        icon="exit_to_app"
      />
      <h1
        class="page-duel__title text-body2 text-uppercase text-primary q-mb-sm"
      >
        <q-item class="text-center rounded-borders bg-grey-2 shadow-2">
          <q-item-section>
            <q-item class="q-pa-none q-pb-sm">
              <q-item-section class="text-right">
                <q-item-label
                  overline
                  class="text-primary"
                >
                  <p>{{ game.awayTeam.city }}</p>
                  <p class="text-bold">
                    {{ game.awayTeam.name }}
                  </p>
                </q-item-label>
              </q-item-section>
              <q-item-section class="col-1 text-primary text-bold">
                {{ $t('@') }}
              </q-item-section>
              <q-item-section class="text-left">
                <q-item-label
                  overline
                  class="text-primary"
                >
                  <p>{{ game.homeTeam.city }}</p>
                  <p class="text-bold">
                    {{ game.homeTeam.name }}
                  </p>
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item-label
              caption
              class="text-bold text-primary"
            >
              <p>{{ gameDate }}</p>
              <p>{{ game.location }}</p>
            </q-item-label>
          </q-item-section>
        </q-item>
      </h1>
      <template
        v-if="
          game.status === GAME_STATUSES.LIVE &&
            duel.status === DUEL_STATUSES.OPEN
        "
      >
        <h2 class="predictions__title text-h6 text-center">
          {{ $t('titles.next_at_bat') }}
        </h2>
        <div
          v-if="nextAtBat"
          class="predictions__pair row justify-between items-center text-center"
        >
          <div class="col-5 text-right">
            <p>{{ nextAtBat.awayPlayer.name }}</p>
            <p>
              {{ game.awayTeam.abbreviation }}
              -
              <template v-if="nextAtBat.awayPlayer.jerseyNumber >= 0">
                #{{ nextAtBat.awayPlayer.jerseyNumber }}
              </template>
              <template v-else>
                {{ $t('NA') }}
              </template>
            </p>
          </div>
          <span class="col-2 text-secondary">{{ $t('vs') }}</span>
          <div class="col-5 text-left">
            <p>{{ nextAtBat.homePlayer.name }}</p>
            <p>
              {{ game.homeTeam.abbreviation }}
              -
              <template v-if="nextAtBat.homePlayer.jerseyNumber >= 0">
                #{{ nextAtBat.homePlayer.jerseyNumber }}
              </template>
              <template v-else>
                {{ $t('NA') }}
              </template>
            </p>
          </div>
        </div>
        <div
          v-else
          class="text-center"
        >
          {{ $t('statuses.waiting_for_batters') }}
        </div>
      </template>
    </header>
    <div class="page-duel__content text-left">
      <!-- LIVE -->
      <q-tab-panels
        v-if="
          game.status === GAME_STATUSES.LIVE &&
            duel.status === DUEL_STATUSES.OPEN
        "
        v-model="duelScreen"
        swipeable
        animated
      >
        <q-tab-panel name="predictions">
          <div class="predictions">
            <div class="predictions__content q-px-sm">
              <ul class="list-actions">
                <li class="q-pb-sm">
                  <q-btn
                    @click="
                      predictionTab = 'HIT';
                      predictionModalOpened = true;
                    "
                    :disabled="!nextAtBat"
                    :color="nextAtBat ? 'positive' : 'grey'"
                    class="full-width"
                    size="lg"
                  >
                    {{ $t('labels.predictions.hit') }}
                  </q-btn>
                </li>
                <li class="q-py-sm">
                  <q-btn
                    @click="
                      predictionTab = 'OUT';
                      predictionModalOpened = true;
                    "
                    :disabled="!nextAtBat"
                    :color="nextAtBat ? 'negative' : 'grey'"
                    class="full-width"
                    size="lg"
                  >
                    {{ $t('labels.predictions.out') }}
                  </q-btn>
                </li>
                <li class="q-pt-sm">
                  <q-btn
                    @click="
                      predictionTab = 'WALK';
                      predictionModalOpened = true;
                    "
                    :disabled="!nextAtBat"
                    :color="nextAtBat ? 'blue' : 'grey'"
                    class="full-width"
                    size="lg"
                  >
                    {{ $t('labels.predictions.walk') }}
                  </q-btn>
                </li>
              </ul>
            </div>
            <footer class="predictions__footer q-mt-md">
              <div
                v-if="
                  currentCompetition &&
                    currentCompetition.status === 'CLOSED' &&
                    currentCompetition.result === null
                "
                class="predictions__waiting text-center"
              >
                <p>{{ $t('statuses.waiting_for_results') }}:</p>
                <p class="text-secondary text-bold">
                  {{ currentCompetition.awayPlayer.name }} vs
                  {{ currentCompetition.homePlayer.name }}
                </p>
              </div>
            </footer>
          </div>
        </q-tab-panel>
        <q-tab-panel name="pending">
          <div
            v-if="pendingCompetitions && pendingCompetitions.length > 0"
            class="page-duel__pending text-left q-py-md"
          >
            <h4 class="text-h6 text-uppercase text-center">
              {{ $t('statuses.pending') }}
            </h4>
            <q-markup-table
              separator="cell"
              bordered
              dense
              class="q-mx-sm"
            >
              <thead>
                <tr>
                  <th>{{ $t('Prediction') }}</th>
                  <th>{{ $t('Points') }}</th>
                </tr>
              </thead>
              <tbody v-for="item in pendingCompetitions">
                <tr>
                  <td
                    colspan="3"
                    class="text-center text-bold"
                  >
                    <p>
                      {{ item.competition.awayPlayer.name }} vs
                      {{ item.competition.homePlayer.name }}
                    </p>
                  </td>
                </tr>
                <tr v-for="prediction in item.predictions">
                  <td>
                    {{ getPredictionLabel(prediction.prediction, prediction) }}
                  </td>
                  <td>{{ prediction.amount }}</td>
                </tr>
              </tbody>
            </q-markup-table>
          </div>
          <div
            v-else
            class="text-center q-py-md text-bold text-body1"
          >
            <p>{{ $t('statuses.no_pending_predictions') }}</p>
          </div>
        </q-tab-panel>
        <q-tab-panel name="results">
          <div
            v-if="pastCompetitions && pastCompetitions.length > 0"
            class="page-duel__results text-left q-py-md"
          >
            <h4 class="text-h6 text-uppercase text-center">
              {{ $t('titles.results') }}
            </h4>
            <q-markup-table
              separator="cell"
              bordered
              dense
              class="q-mx-sm"
            >
              <thead>
                <tr>
                  <th>{{ $t('Prediction') }}</th>
                  <th>{{ $t('Points') }}</th>
                  <th>{{ $t('Result') }}</th>
                </tr>
              </thead>
              <tbody v-for="item in pastCompetitions">
                <tr>
                  <td
                    colspan="3"
                    class="text-center text-bold"
                  >
                    <p>
                      {{ item.competition.awayPlayer.name }} vs
                      {{ item.competition.homePlayer.name }}
                    </p>
                    <p>
                      {{ getPredictionLabel(item.competition.result) }}
                    </p>
                  </td>
                </tr>
                <tr v-for="prediction in item.predictions">
                  <td>
                    {{ getPredictionLabel(prediction.prediction, prediction) }}
                  </td>
                  <td>{{ prediction.amount }}</td>
                  <td>
                    <q-icon
                      :name="getPredictionIcon(prediction.status)"
                      :color="getPredictionIconColor(prediction.status)"
                    />
                  </td>
                </tr>
              </tbody>
            </q-markup-table>
          </div>
          <div
            v-else
            class="text-center q-py-md text-bold text-body1"
          >
            <p>{{ $t('statuses.no_results') }}</p>
          </div>
        </q-tab-panel>
        <q-tab-panel name="standings">
          <div class="page-duel__standings">
            <h4 class="text-h6 text-uppercase text-center q-mb-sm">
              {{ $t('titles.standings') }}
            </h4>
            <div class="q-px-sm">
              <q-markup-table
                separator="cell"
                bordered
                dense
                class="q-mx-sm"
              >
                <thead>
                  <tr>
                    <th style="width: 30px;">
                      {{ $t('labels.rank') }}
                    </th>
                    <th>{{ $t('labels.user') }}</th>
                    <th>{{ $t('labels.points') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in standings.standings"
                    :class="{
                      'bg-green-2': row.user.username === user.username,
                    }"
                  >
                    <td>{{ row.rank }}</td>
                    <td>{{ row.user.username }}</td>
                    <td>{{ row.points }}</td>
                  </tr>
                </tbody>
              </q-markup-table>
            </div>
          </div>
        </q-tab-panel>
      </q-tab-panels>

      <!-- SCHEDULED -->
      <div
        v-if="game.status === GAME_STATUSES.SCHEDULED"
        class="page-duel__starts-soon text-center text-body1"
      >
        <countdown :time="countdownTime">
          <template slot-scope="props">
            <i18n
              path="statuses.duel_starts_soon.msg"
              tag="div"
            >
              <div
                class="text-primary text-uppercase text-bold"
                place="duelers"
              >
                <p>
                  {{ $t('statuses.duel_starts_soon.duelers', [duel.type]) }}
                </p>
              </div>
              <div
                class="text-primary text-uppercase text-bold"
                place="teams"
              >
                <p>
                  {{
                    $t('statuses.duel_starts_soon.teams', [
                      getTeamFullName(game.awayTeam),
                      getTeamFullName(game.homeTeam),
                    ])
                  }}
                </p>
              </div>
              <span place="time">
                <template v-if="props.totalMinutes <= COUNTDOWN_MINUTES">
                  {{ $t('statuses.duel_starts_soon.in') }}:
                </template>
                <template v-else>
                  {{ $t('statuses.duel_starts_soon.soon') }}
                </template>
              </span>
            </i18n>
            <div
              v-if="props.totalMinutes <= COUNTDOWN_MINUTES"
              class="text-primary text-bold"
            >
              {{ props.minutes | leftPad }}:{{ props.seconds | leftPad }}
            </div>
          </template>
        </countdown>
      </div>
      <div
        v-if="duel.status === DUEL_STATUSES.CLOSED"
        class="page-duel__standings"
      >
        <h4 class="text-h6 text-uppercase text-center q-mb-sm">
          {{ $t('titles.standings') }}
        </h4>
        <div class="q-px-sm">
          <q-markup-table
            separator="cell"
            bordered
            dense
            class="q-mx-sm"
          >
            <thead>
              <tr>
                <th style="width: 30px;">
                  {{ $t('labels.rank') }}
                </th>
                <th>{{ $t('labels.user') }}</th>
                <th>{{ $t('labels.points') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in standings.standings"
                :class="{ 'bg-green-2': row.user.username === user.username }"
              >
                <td>{{ row.rank }}</td>
                <td>{{ row.user.username }}</td>
                <td>{{ row.points }}</td>
              </tr>
            </tbody>
          </q-markup-table>
        </div>
      </div>
    </div>
    <footer
      v-if="
        duel.status === DUEL_STATUSES.OPEN && game.status === GAME_STATUSES.LIVE
      "
      class="page-duel__footer site-width"
    >
      <nav class="page-duel__nav">
        <q-tabs
          v-model="duelScreen"
          dense
          align="center"
          switch-indicator
          narrow-indicator
          no-caps
          class="bg-grey-1 shadow-2"
        >
          <q-tab
            name="predictions"
            icon="playlist_add"
            label="Predict"
            class="text-primary"
          />
          <q-tab
            name="pending"
            icon="access_time"
            label="Pending"
            class="text-orange-10"
          >
            <q-badge
              v-if="
                pendingCompetitions &&
                  pendingCompetitions.length > 0 &&
                  pendingCompetitions[0].predictions.length > 0
              "
              color="grey"
              floating
            >
              {{ pendingCompetitions[0].predictions.length }}
            </q-badge>
          </q-tab>
          <q-tab
            name="results"
            icon="playlist_add_check"
            label="Results"
            class="text-blue-8"
          />
          <q-tab
            name="standings"
            icon="list"
            label="Standings"
            class="text-red-8"
          />
        </q-tabs>
      </nav>
    </footer>
  </div>
  <div v-else>
    <q-btn
      @click="leaveDuel"
      :label="$t('buttons.leave_the_duel')"
      color="negative"
      class="btn-leave-duel"
      dense
      flat
      icon="exit_to_app"
    />
  </div>
</template>
<script>
import Vue from 'vue';
import i18n from '@/localization';
import { Notify } from 'quasar';
import Component from 'vue-class-component';
import api from '@/services';
import store from '@/store';
import { mapGetters, mapActions } from 'vuex';
import { isFinite } from 'lodash';
import edtDate from '@/helpers/edt-date';
import { POINTS_FIXED_LIST, GAME_STATUSES, COUNTDOWN_MINUTES } from '@/helpers';
import { DUEL_STATUSES, PREDICTION_LABELS } from '@/helpers/constants';
import { format } from 'date-fns';
import SelectablePrediction from '@/components/common/SelectablePrediction';
import SelectablePoints from '@/components/common/SelectablePoints';

@Component({
  name: 'page-duel',
  components: {
    SelectablePrediction,
    SelectablePoints,
  },
  filters: {
    leftPad(val) {
      return ('00' + val).slice(-2);
    },
  },
  computed: mapGetters([
    'games',
    'user',
    'stompConfig',
    'stompClient',
    'activeGameId',
    'activeDuelId',
    'isLoggedIn',
    'isProcessing',
    'gameState',
    'predictionsTypes',
    'predictionsTypesParsed',
  ]),
  watch: {
    $route: {
      async handler(val) {
        const { id, duelId } = val.params;
        store.dispatch('toggleProcessing', true);
        const game = await this.$api.Game.getGame(id);

        // GAME COMPLETED
        if (game.status === GAME_STATUSES.COMPLETED) {
          this.localGameState = await this.getGameState(id);
          store.dispatch('toggleProcessing', false);
          return;
        }

        this.localGameState = null;
        store.dispatch('toggleProcessing', false);
      },
      deep: true,
    },
    predictionTab(val) {
      switch (val) {
        case 'HIT':
        case 'WALK':
          this.selectedPrediction = this.predictionsTypesParsed[
            val
          ].items[0].code;
          break;
        case 'OUT':
          this.predictionOutTab = 'GROUND_OUT';
          this.selectedPrediction = this.predictionsTypesParsed['OUT'][
            'GROUND_OUT'
          ].items[0].code;
      }
    },
    predictionOutTab(val) {
      this.selectedPrediction = this.predictionsTypesParsed['OUT'][
        val
      ].items[0].code;
    },
    nextAtBat: {
      handler(val) {
        if (val === null && this.predictionModalOpened === true) {
          this.predictionModalOpened = false;
          this.$q.notify({
            icon: 'report_problem',
            color: 'warning',
            textColor: 'black',
            message: this.$t('messages.competition_closed'),
          });
        }
      },
      deep: false,
    },
  },
  methods: mapActions(['toggleProcessing', 'resetGameState', 'getGameState']),
  async beforeRouteEnter(to, from, next) {
    const { isLoggedIn, activeGameId, activeDuelId } = store.getters;

    // UNAUTHORIZED ACCESS
    if (!isLoggedIn) next('/');

    const { id, duelId } = to.params;

    // INVALID ROUTE PARAMS
    if (!isFinite(+id) || !isFinite(+duelId)) {
      next('/');
      return;
    }

    store.dispatch('toggleProcessing', true);

    let game;
    let duel;

    try {
      const payload = await Promise.all([
        api.Game.getGame(id),
        api.Duel.getDuel(duelId),
      ]);
      game = payload[0];
      duel = payload[1];
    } catch (err) {
      Notify.create({
        icon: 'report_problem',
        color: 'warning',
        textColor: 'black',
        message: i18n.t('messages.game_no_access'),
      });
      next(from);
      store.dispatch('toggleProcessing', false);
      return;
    }

    // COMPLETED STATE
    if (
      duel.status === DUEL_STATUSES.CLOSED ||
      game.status === GAME_STATUSES.COMPLETED
    ) {
      const gameState = await store.dispatch('getGameState', id);
      next(vm => {
        vm.localGameState = gameState;
      });
      store.dispatch('toggleProcessing', false);
      return;
    }

    // SCHEDULED OR LIVE

    // USER HAS ACTIVE DUEL
    if (activeGameId && activeDuelId) {
      // WRONG GAME
      if (+id !== activeGameId) {
        Notify.create({
          icon: 'report_problem',
          color: 'warning',
          textColor: 'black',
          message: i18n.t('messages.duplicate_duels'),
        });
        next(from);
        store.dispatch('toggleProcessing', false);
        return;
      }

      // PROPER GAME BUT WRONG DUEL
      if (+duelId !== +activeDuelId) {
        Notify.create({
          icon: 'report_problem',
          color: 'warning',
          textColor: 'black',
          message: i18n.t('messages.duel_no_access'),
        });
        next({
          name: 'game',
          params: { id },
        });
        return;
      }

      next();
      store.dispatch('toggleProcessing', false);
      return;
    }

    // NO ACTIVE DUEL
    try {
      await api.Duel.assignTo(duelId);
      await store.dispatch('getGameState');
      Notify.create({
        message: i18n.t('Your Duel has started! Start playing!'),
        classes: 'text-center',
      });
      next();
    } catch (err) {
      Notify.create({
        icon: 'report_problem',
        color: 'negative',
        message: err.toString(),
      });
      next('/');
    }
    store.dispatch('toggleProcessing', false);
  },
})
export default class PageGameDuel extends Vue {
  // !DATA
  GAME_STATUSES = GAME_STATUSES;
  DUEL_STATUSES = DUEL_STATUSES;
  POINTS_FIXED_LIST = POINTS_FIXED_LIST;
  COUNTDOWN_MINUTES = COUNTDOWN_MINUTES;
  PREDICTION_LABELS = PREDICTION_LABELS;
  today = new Date();
  countdownTime = null;
  localGameState = null;
  predictionModalOpened = false;
  columns = [
    {
      name: 'rank',
      label: i18n.t('labels.rank'),
      sortable: true,
      style: 'width: 30px',
      field: row => row.rank,
    },
    {
      name: 'user',
      label: i18n.t('labels.user'),
      field: row => row.user.username,
    },
    {
      name: 'points',
      label: i18n.t('labels.points'),
      field: row => row.points,
    },
  ];

  duelScreen = 'predictions';
  predictionTab = '';
  predictionOutTab = '';

  selectedPrediction = '';
  selectedPoints = '';

  activeColors = {
    HIT: 'green',
    OUT: 'red',
    WALK: 'blue',
  };

  // !COMPUTED
  get userStanding() {
    if (!this.standings) return null;
    const standing =
      this.standings.standings.find(
        ({ user }) => user.username === this.user.username
      ) || null;
    return standing;
  }
  get userRank() {
    return (this.userStanding && this.userStanding.rank) || null;
  }
  get userCurrentPoints() {
    return (this.userStanding && this.userStanding.points) || 0;
  }
  get isSelectedCustomPoints() {
    return (
      this.selectedPoints !== '' &&
      !POINTS_FIXED_LIST.includes(+this.selectedPoints)
    );
  }
  get makePredictionDisabled() {
    return (
      this.selectedPrediction === '' ||
      this.selectedPoints === '' ||
      this.selectedPoints > this.userCurrentPoints
    );
  }
  get gameId() {
    return this.$route.params.id;
  }
  get duelId() {
    return this.$route.params.duelId;
  }
  get game() {
    return (
      (this.localGameState && this.localGameState.activeGame) ||
      (this.gameState && this.gameState.activeGame)
    );
  }
  get duel() {
    return (
      (this.localGameState && this.localGameState.activeDuel) ||
      (this.gameState && this.gameState.activeDuel)
    );
  }
  get calcPoints() {
    let result = 500;
    this.allCompetitions.forEach(({ competition, predictions }) => {
      predictions.forEach(({ amount, ratio, status }) => {
        if (['INCORRECT', 'PENDING'].includes(status)) {
          result -= amount;
        } else if (status === 'CORRECT') {
          result += amount * ratio;
        }
      });
    });
    return result;
  }
  get pastCompetitions() {
    return (
      this.gameState &&
      this.gameState.pastCompetitions &&
      this.gameState.pastCompetitions
        .slice()
        .sort((a, b) => {
          if (a.competition.id < b.competition.id) {
            return -1;
          }
          if (a.competition.id > b.competition.id) {
            return 1;
          }
        })
        .reverse()
    );
  }
  get pendingCompetitions() {
    return this.gameState.pendingCompetitions;
  }
  get allCompetitions() {
    return this.pastCompetitions.concat(this.pendingCompetitions);
  }
  get standings() {
    return (
      (this.localGameState && this.localGameState.standings) ||
      (this.gameState && this.gameState.standings)
    );
  }
  get gameId() {
    return +this.$route.params.id;
  }
  // get game() {
  //   return this.games && this.games.find(({ id }) => +this.gameId === +id);
  // }
  get duelId() {
    return +this.$route.params.duelId;
  }
  get canLeaveDuel() {
    return this.activeDuelId === this.duelId;
  }
  get gameDate() {
    return edtDate(this.game.startDate, 'M.D.YY - h:mm A [EDT]');
  }
  get activeGame() {
    return this.gameState.activeGame;
  }
  get currentInning() {
    return this.activeGame && this.activeGame.currentInning;
  }
  get activeCompetitions() {
    return (
      (this.currentInning &&
        this.currentInning.competitions.filter(
          ({ status, inningStatus }) =>
            status !== 'DONE' && inningStatus === this.currentInning.status
        )) ||
      []
    );
  }
  get currentCompetition() {
    if (this.activeCompetitions.length === 1) {
      return this.activeCompetitions[0];
    }
    if (this.activeCompetitions.length === 2) {
      return this.activeCompetitions.find(({ status }) => status === 'CLOSED');
    }
    return this.activeCompetitions.find(({ status }) => status === 'CLOSED');
  }
  get onDeckCompetition() {
    if (this.activeCompetitions.length === 1) {
      return null;
    }
    if (this.activeCompetitions.length === 2) {
      return this.activeCompetitions.find(({ status }) =>
        ['PENDING', 'OPEN'].includes(status)
      );
    }
    return this.activeCompetitions.find(({ status }) =>
      ['PENDING', 'OPEN'].includes(status)
    );
  }
  get nextAtBat() {
    if (this.onDeckCompetition) return this.onDeckCompetition;
    if (!this.currentCompetition) return null;
    if (['PENDING', 'OPEN'].includes(this.currentCompetition.status)) {
      return this.currentCompetition;
    }
    return null;
  }
  get pendingPredictions() {
    return this.activeGame.pendingPredictions;
  }
  get gameNotStarted() {
    return (
      this.game.status === GAME_STATUSES.SCHEDULED ||
      (this.currentInning &&
        this.currentInning.number === 1 &&
        this.currentInning.status === 'TOP' &&
        this.currentInning.competitions.length === 0)
    );
  }

  // !HOOKS
  async created() {
    await this.$nextTick();
    this.today.setMilliseconds(0);
    this.$eventHub.$on('duels-update', this.onDuelsUpdate);
    this.countdownTime = new Date(this.game.startDate) - this.today;
  }

  // !METHODS
  openCustomPointsModal() {
    this.$q
      .dialog({
        title: this.$t('titles.custom_points'),
        message: this.$t('messages.custom_points_cta', 15),
        prompt: {
          model: '',
          type: 'number',
        },
        cancel: true,
        persistent: true,
      })
      .onOk(data => {
        const points = parseInt(data);
        let errorMessage;
        if (isNaN(points) || points <= 0) {
          errorMessage = this.$t('messages.invalid_points');
        }
        if (points > this.userCurrentPoints) {
          errorMessage = this.$t('messages.not_enough_points');
        }
        if (errorMessage) {
          this.$q.notify({
            color: 'warning',
            textColor: 'black',
            classes: 'text-center',
            message: errorMessage,
          });
          return;
        }
        this.selectedPoints = +points;
      });
  }
  getTabLabel(str) {
    return PREDICTION_LABELS[str] || str;
  }
  async onDuelsUpdate({ game, duels }) {
    if (+game.id !== +this.$route.params.id) return;
    const duel = duels.find(({ id }) => +id === +this.$route.params.duelId);
    if (!duel) return;
    if (duel.status === DUEL_STATUSES.OPEN) return;
    this.toggleProcessing(true);
    try {
      this.localGameState = await this.getGameState(this.gameId);
      await this.getGameState();
      this.$q.dialog({
        title: this.$t('titles.duel_closed'),
        message: `
          <p>${this.$t('messages.duel_closed')}</p>
          <a href="http://bit.ly/dduelsurvey" target="_blank" class="text-bold">${this.$t(
            'labels.link_for_the_survey'
          )}</a>
        `,
        html: true,
      });
    } catch (err) {
      this.$router.replace({ path: '/' });
    }
    this.toggleProcessing(false);
  }
  getLogoUrl(name) {
    return `${this.$appConfig.bucket_url}/${name.toLowerCase()}.svg`;
  }
  getTeamFullName({ city, name }) {
    return `${city} ${name}`;
  }
  getPredictionLabel(name) {
    if (name === 'VOID_RESULTS') return this.$t('game_actions.void_results');
    const parts = name.split('|');
    const group = parts[parts.length - 2].split('@')[0];
    const title = parts[parts.length - 1].split('@')[0];

    if (group === title)
      return this.$t(`prediction_types.${title.toLowerCase()}`);
    return `${this.$t(
      `prediction_types.${group.toLowerCase()}`
    )} > ${this.translateWithDefault(
      `prediction_types.${title.toLowerCase()}`,
      title
    )}`;
  }
  translateWithDefault(str, defaultStr) {
    const $te = this.$te.bind(this);
    return this.$t && $te
      ? $te(str)
        ? this.$t(str)
        : defaultStr
      : defaultStr
      ? defaultStr
      : str;
  }
  getPredictionIcon(status) {
    switch (status) {
      case 'CORRECT':
        return 'check_circle';
      case 'VOIDED':
        return 'block';
      case 'INCORRECT':
        return 'cancel';
      case 'PENDING':
        return 'more_horiz';
    }
  }
  getPredictionIconColor(status) {
    switch (status) {
      case 'CORRECT':
        return 'positive';
      case 'VOIDED':
        return 'warning';
      case 'INCORRECT':
        return 'negative';
      case 'PENDING':
        return 'white';
    }
  }
  async makePrediction() {
    this.stompClient.publish({
      destination: this.stompConfig.actionsOutboundQueue,
      body: JSON.stringify({
        competitionId: +this.nextAtBat.id,
        duelId: +this.duelId,
        sid: this.user.sid,
        amount: +this.selectedPoints,
        prediction: this.selectedPrediction,
        utcTimestamp: Date.now(),
      }),
      headers: {
        action: 'PREDICTION',
        token: this.$ls.get('token'),
      },
    });
    this.predictionTab = 'HIT';
    this.predictionOutTab = '';
    this.selectedPrediction = '';
    this.selectedPoints = '';
    this.predictionModalOpened = false;
  }
  getEdtDate(date, template) {
    return edtDate(date, template);
  }
  formatDate(date, template = 'h:mma') {
    return format(date, template);
  }
  async leaveDuel() {
    this.$q
      .dialog({
        title: this.$t('titles.leaving_the_duel'),
        message: this.$t('messages.leave_the_duel'),
        ok: {
          label: this.$t('Yes'),
          color: 'negative',
        },
        cancel: {
          label: this.$t('No'),
          textColor: 'black',
          color: 'white',
        },
        persistent: true,
      })
      .onOk(async () => {
        this.toggleProcessing(true);
        try {
          await this.$api.Duel.unassignFrom(this.duelId);
          this.$q.notify({
            message: this.$t('messages.left_duel'),
            classes: 'text-center',
          });
          this.$ls.remove('gameState');
          this.resetGameState();
          this.$router.replace({
            name: 'game',
            params: { id: this.gameId },
          });
        } catch (err) {
          Notify.create({
            icon: 'report_problem',
            color: 'negative',
            message: i18n.t(err.toString()),
          });
        }
        this.toggleProcessing(false);
      });
  }
}
</script>
<style lang="scss">
.grid-predictions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  & > li {
    flex: 0 0 calc(33.3333% - 4px);
    overflow: hidden;
    margin: 2px;
    & > * {
      display: block;
      width: 100%;
    }
  }
  &.columns-2 {
    & > li {
      flex-basis: calc(50% - 4px);
    }
  }
  &.solo-first-item {
    & > li:first-child {
      flex: 0 0 calc(100% - 4px);
    }
  }
  &.solo-last-item {
    & > li:last-child {
      flex: 0 0 calc(100% - 4px);
    }
  }
  .q-btn {
    line-height: 1.4em;
  }
}
.page-duel {
  &__starts-soon {
    display: flex;
    flex-direction: column;
    justify-content: center;
    line-height: 2em;
  }
  .page {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  &__content {
    flex: 1 1 auto;
    height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    & > * {
      flex: 1;
    }
  }
  &__header {
    padding: 0 (5px + 42px + 5px) 1em;
  }
}
.list-points {
  display: flex;
  // justify-content: space-between;
  & > li {
    // flex: 1;
    // max-width: calc(100% / 6);
    margin: 0 5px;
    &:first-child {
      margin-left: 0;
    }
    &:last-child {
      margin-right: 0;
    }
  }
}
.q-tab-panel {
  padding: 0;
}
.modal-prediction {
  display: flex;
  flex-direction: column;
  &__tabs {
    flex-shrink: 0;
  }
  &__panel {
    flex: 1;
  }
  &__header {
    @media screen and (max-height: 580px) {
      display: none;
    }
  }
  .grid-predictions {
    @media screen and (max-height: 580px) {
      .q-btn {
        font-size: 12px !important;
      }
    }
  }
  .q-tab__content {
    min-width: 0;
  }
  .q-tab__alert {
    right: 100%;
    margin-right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }
  &__points {
    margin-top: auto;
    border-top: 1px solid #ccc;
  }
  &__panels {
    flex-grow: 1;
    .q-tab-panel {
      display: flex;
      flex-direction: column;
      & > .grid-predictions {
        margin-top: auto;
        margin-bottom: auto;
      }
      .q-tab-panels {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        justify-content: center;
      }
    }
  }
}
.q-tabs__content {
  flex-shrink: 0;
}
.tabs-fixed {
  & > .q-tabs__content > .q-tab {
    flex-basis: 100%;
  }
}
</style>
<i18n>
{
  "en": {
    "labels": {
      "link_for_the_survey": "Link for the survey"
    },
    "buttons": {
      "make_prediction": "Make Prediction",
      "cancel": "Cancel",
      "leave_the_duel": "Leave the Duel",
      "hit": "Hit",
      "out": "Out",
      "walk": "Walk"
    },
    "titles": {
      "custom_points": "Custom Points Amount",
      "points": "Points",
      "next_at_bat": "Next at-bat",
      "results": "Results",
      "standings": "Standings",
      "leaving_the_duel": "Leaving the Duel",
      "modal_prediction_title": "Choose your prediction for",
      "duel_closed": "Duel Completed"
    },
    "statuses": {
      "points": "You have: {points} Points",
      "rank": "Rank: {rank}",
      "pending": "Pending",
      "no_pending_predictions": "You don't have pending predictions.",
      "waiting_for_batters": "Waiting for batters...",
      "waiting_for_results": "Waiting for results on",
      "duel_starts_soon": {
        "msg": "Your Duel with {duelers} for the {teams} will start {time}",
        "duelers": "{0} Duelers",
        "teams": "{0} @ {1}",
        "in": "in",
        "soon": "soon"
      },
      "no_results": "Please, post some predictions to see results!"
    },
    "messages": {
      "join_duel": "You have joined the duel. Have fun!",
      "left_duel": "You have left the duel. We hope you enjoyed your experience!",
      "leave_the_duel": "The Duel is still in progress. Are you sure you want to leave?",
      "game_no_access": "You don't have an access to other games while you have an active one.",
      "duel_no_access": "You don't have an access to other duels while you have an active one.",
      "competition_closed": "Sorry – Predictions for this batter are closed!",
      "duel_closed": "Thanks for playing Diamond Duels! We would appreciate if you would take 2 minutes to share your thoughts to help us improve!",
      "invalid_points": "Invalid points amount",
      "not_enough_points": "Not enough points",
      "custom_points_cta": "Put your custom points amount"
    },
    "prediction_types": {
      "hit": "HIT",
      "single": "Single",
      "double": "Double",
      "triple": "Triple",
      "home_run": "HOME RUN",
      "out": "OUT",
      "fly_out": "FLY OUT",
      "ground_out": "GROUND OUT",
      "go789": "LF, CF or RF",
      "walk": "Walk/HBP",
      "strike_out": "STRIKEOUT",
      "any": "Any"
    }
  }
}
</i18n>
